import LargeTile from "@components/LargeTile";
import LBox from "@components/LBox";
import LFlatList from "@components/LFlatList";
import LScreen from "@components/LScreen";
import LText from "@components/LText";
import { LoadingScreen } from "@components/Search/BrowseGenre/LoadingScreen";
import { useSetlists } from "@hooks/trpc/useSetlists";
import { theme } from "@theme";
import { keyExtractor } from "@utils/data";
import React, { useEffect, useMemo, useRef } from "react";

import { ActivityIndicator, useWindowDimensions } from "react-native";
import { getViewPort } from "@utils/web/config";
import { captureException } from "@utils/captureException";

const NUMBER_OF_COLUMNS = 3;

// The DJ Sets category browse — the same grid as BrowseGenreScreen, fed by the
// setlists feed instead of a genre id (see djSetsCategory.ts for the tile).
export const BrowseSetlistsScreen = () => {
  const window = useWindowDimensions();
  const { width } = getViewPort(window);

  const {
    query: { data: setlistPages, fetchNextPage, isFetchingNextPage },
  } = useSetlists();

  // Note: onEndReached can be triggered multiple times in very quick succession during
  // a list scroll. However, we don't want to repeat the same fetch queries for the
  // same cursor, so we use a ref to flag if a fetch has previously been triggered.
  const endReachedRef = useRef(false);
  useEffect(() => {
    endReachedRef.current = isFetchingNextPage;
  }, [isFetchingNextPage]);

  const handleEndReached = () => {
    if (endReachedRef.current === false) {
      endReachedRef.current = true;
      fetchNextPage().catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "pagination",
          operation: "fetch-next-search-setlists",
        });
      });
    }
  };

  const setlistAlbums = useMemo(() => {
    return setlistPages?.pages.flatMap((page) => page.data) || [];
  }, [setlistPages]);

  if (!setlistPages) {
    return <LoadingScreen />;
  }

  // Note: The tileSize is calculated as the screen width (coming from the LScreen container),
  // together with the LScreen component's paddingHorizontal of "s4" (left and right) and the
  // LFlatList columnGap of s4 (two of these since the NUMBER_OF_COLUMNS is set to 3).
  const tileSize =
    (width - (2 + NUMBER_OF_COLUMNS - 1) * theme.spacing.s4) /
    NUMBER_OF_COLUMNS;

  return (
    <LScreen enableAutomaticTopPadding={true}>
      <LFlatList
        data={setlistAlbums}
        numColumns={NUMBER_OF_COLUMNS}
        showsVerticalScrollIndicator={false}
        keyExtractor={keyExtractor}
        style={{
          alignSelf:
            (setlistAlbums?.length ?? 0) < NUMBER_OF_COLUMNS
              ? "flex-start"
              : "center",
          // width has to be set to 100%. otherwise the loading indicator will not show in the middle
          width: "100%",
        }}
        columnWrapperStyle={{
          columnGap: theme.spacing.s4,
        }}
        contentContainerStyle={{
          rowGap: theme.spacing.m1,
        }}
        renderItem={({ item: collection }) => (
          <LargeTile
            key={collection.id}
            item={collection}
            screen={"explore"}
            size={tileSize}
          />
        )}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.35}
        ListFooterComponent={
          isFetchingNextPage ? (
            <ActivityIndicator
              size="large"
              style={{ paddingBottom: theme.spacing.s4 }}
            />
          ) : null
        }
        ListEmptyComponent={
          <LBox gap="s2" alignItems="center" style={{ marginTop: 146 }}>
            <LText variant="menuButton">No results</LText>
            <LText textAlign="center" variant="inputTitle" color="chartLine">
              {"No DJ sets found yet.\nCheck back soon"}
            </LText>
          </LBox>
        }
      />
    </LScreen>
  );
};
